import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { FileUp } from 'lucide-react';
import { Btn } from '../platform/ui';
import {
  MODEL_ACCEPT, MODEL_FORMATS, MAX_MODEL_BYTES, formatOf, baseName,
} from '../../lib/modelFormats';

/** Разворачивает выбранные файлы в список моделей: ZIP раскрывается, остальное берётся как есть. */
export const collectModels = async (fileList, t) => {
  const out = [];
  for (const file of Array.from(fileList)) {
    const format = formatOf(file.name);
    if (!format) { toast.error(t('modelImport.unsupported', { name: file.name })); continue; }
    if (file.size > MAX_MODEL_BYTES) {
      toast.error(t('modelImport.tooBig', { mb: Math.round(MAX_MODEL_BYTES / 1024 / 1024), name: file.name }));
      continue;
    }
    if (format !== 'zip') { out.push({ name: baseName(file.name), format, blob: file }); continue; }
    try {
      const JSZip = (await import('jszip')).default;
      const zip = await JSZip.loadAsync(file);
      const entries = Object.values(zip.files)
        .filter((e) => !e.dir && !e.name.startsWith('__MACOSX') && MODEL_FORMATS.includes(formatOf(e.name)))
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
      if (!entries.length) { toast.error(t('modelImport.zipEmpty', { name: file.name })); continue; }
      for (const entry of entries) {
        out.push({ name: baseName(entry.name), format: formatOf(entry.name), blob: await entry.async('blob') });
      }
    } catch (e) {
      toast.error(t('modelImport.zipEmpty', { name: file.name }));
    }
  }
  return out;
};

/** Загрузка собственных 3D-моделей в браузер: файлы никуда не отправляются, просмотр идёт локально. */
const ModelImport = ({ onModels, disabled }) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);

  const handle = async (fileList) => {
    if (!fileList || !fileList.length) return;
    setBusy(true);
    try {
      const models = await collectModels(fileList, t);
      if (models.length) {
        onModels(models);
        toast.success(t('modelImport.added', { count: models.length }));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); if (!disabled) handle(e.dataTransfer.files); }}
      className={`border-2 border-dashed p-6 text-center transition-colors ${over ? 'border-[#C41E3A] bg-red-50' : 'border-black bg-white'}`}
      data-testid="model-import"
    >
      <FileUp className="mx-auto mb-2" size={30} />
      <p className="text-lg font-bold mb-1">{over ? t('modelImport.dropHere') : t('modelImport.title')}</p>
      <p className="text-sm text-gray-600 mb-4 max-w-2xl mx-auto">{t('modelImport.hint')}</p>
      <Btn variant="secondary" disabled={disabled || busy} loading={busy} onClick={() => inputRef.current?.click()}>
        {t('modelImport.choose')}
      </Btn>
      <input ref={inputRef} type="file" multiple accept={MODEL_ACCEPT} className="hidden"
             onChange={(e) => { handle(e.target.files); e.target.value = ''; }} />
    </div>
  );
};

export default ModelImport;
